import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Api,
  AgentInfo,
  EnrollmentTokenResult,
  GroupInfo,
  MyProfile,
  OrgEntryInfo,
  PendingClaim,
} from '../api';
import { useConnectPassword } from './ConnectPassword';
import type { ConnectOptions } from '../sessions/types';
import { confirmAction, confirmText } from '../ui/confirm';
import { isSameDeviceCode, useMyDeviceCode } from './myConnection/useMyDeviceCode';
import {
  buildTree,
  canAddChild,
  groupDeleteProblem,
  groupMoveProblem,
  groupPath,
  GROUP_TREE_MAX_DEPTH,
  loadCollapsed,
  saveCollapsed,
  type GroupNode,
} from '../devices/groupTree';
import type { TreeDragItem } from '../devices/treeDnd';
import { GroupTree } from './devices/GroupTree';
import { GroupMenu, type MenuItem } from './devices/GroupMenu';
import { GroupPicker, ROOT_OPTION } from './devices/GroupPicker';
import { EntryForm, NameForm } from './devices/InlineForms';
import { AgentRow } from './devices/AgentRow';
import { EntryRow } from './devices/EntryRow';
import type { RenameState } from './devices/RenameField';
import { EnrollTokenNotice } from './devices/EnrollTokenNotice';
import { useAddressBook } from './devices/useAddressBook';

/**
 * 기기 등록 요청 목록 폴링 주기(ms). 사람이 다른 기기 앞에서 요청을 보내고 "수락해 달라"고 말하는
 * 흐름이라 몇 초 지연은 문제가 되지 않는다 — 짧게 잡아 서버를 두드릴 이유가 없다.
 */
const CLAIM_POLL_MS = 15000;

/**
 * 그룹 메뉴 [이 그룹에 기기 등록](등록 토큰 발급)을 보일지(2026-10-12 사용자 결정 — **숨김**).
 * Windows·Mac·Android 는 앱의 [조직에 등록] → 관리자 수락으로 들어오므로 쓸 일이 없고, 환경변수 방식이 불편하다.
 * 화면 없는 Linux 기기 설치(`install.sh --enroll-token`)에 필요해지면 이 값만 true 로 — 서버 API·토큰 안내 화면은 그대로 남아 있다.
 */
const SHOW_GROUP_ENROLL_TOKEN = false;

/** 접힘 상태 저장 범위 — 그룹 id 가 조직마다 달라 키 하나로 충분하다. */
const COLLAPSE_SCOPE = 'devices';

/** 그룹 머리 아래에 펼치는 입력(한 번에 하나). groupId = null 이면 최상위 새 그룹. */
type GroupForm = { groupId: string | null; kind: 'child' | 'rename' | 'entry' | 'move' };

/** 브라우저 저장소(막혀 있으면 null — 접힘 기억은 편의 기능). */
function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * 기기 화면 = **그룹 트리**(설계서 §14.20, 2026-10-06 사용자 결정) — 조직 그룹 아래 🖥 관리 기기와 🔖 공유 ID 항목,
 * 맨 아래 "내 주소록"(개인 ID 항목). 종전 그룹 필터·그룹 관리 패널·토큰 패널·주소록 탭을 이 한 화면으로 합쳤다.
 *
 * @param isAdmin 관리자면 그룹 메뉴·끌어다 놓기·이동·차단 단추를 보인다. 숨김은 편의일 뿐 — 권한 검사는 서버가 한다.
 */
export function DevicesView({
  api,
  onConnect,
  busy,
  isAdmin,
}: {
  api: Api;
  onConnect: (target: string, password?: string, label?: string, opts?: ConnectOptions) => void;
  busy: boolean;
  isAdmin: boolean;
}) {
  // 접속 비밀번호 입력(공통 조각) — 관리 기기는 id 로, ID 항목은 코드로 같은 조각을 쓴다.
  const pw = useConnectPassword(onConnect, () => api.token);
  // 이 앱이 돌고 있는 PC 의 코드 — 목록에는 보이되 "이 PC" 로 표시하고 접속은 막는다(2026-09-29 사용자 요청).
  const myCode = useMyDeviceCode();
  const book = useAddressBook(api);
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [entries, setEntries] = useState<OrgEntryInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // 진행 중인 조작의 열쇠(행·그룹) — 그 자리의 단추만 잠근다.
  const [busyKey, setBusyKey] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(safeStorage(), COLLAPSE_SCOPE));
  const [groupForm, setGroupForm] = useState<GroupForm | null>(null);
  // 그룹 이동 고르기를 연 항목('agent:<id>' | 'entry:<id>').
  const [moving, setMoving] = useState('');
  // 그룹 메뉴 [이 그룹에 기기 등록] 으로 받은 토큰(1회만 보인다).
  const [tokenShown, setTokenShown] = useState<{ groupId: string; token: EnrollmentTokenResult } | null>(null);
  const [bookForm, setBookForm] = useState(false);

  // 기기 등록 요청(claim) — 기기가 "내 조직에 넣어 달라"고 보낸 것. 내 이메일이 대상 주소다.
  const [claims, setClaims] = useState<PendingClaim[]>([]);
  const [me, setMe] = useState<MyProfile | null>(null);
  const [claimBusyId, setClaimBusyId] = useState('');
  // 관리자가 수락할 때 그룹을 고르는 중인 요청.
  const [claimPicking, setClaimPicking] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 그룹·ID 항목·등록 요청·내 프로필은 실패해도 기기는 그려야 하므로 각자 삼킨다(옛 서버에는 경로가 없을 수 있다).
      const [ags, grs, ens, cls, prof] = await Promise.all([
        api.getAgents(),
        api.getGroups().catch(() => [] as GroupInfo[]),
        api.getOrgEntries().catch(() => [] as OrgEntryInfo[]),
        api.getPendingClaims().catch(() => [] as PendingClaim[]),
        api.getMe().catch(() => null),
      ]);
      setAgents(ags);
      setGroups(grs);
      setEntries(ens);
      setClaims(cls);
      setMe(prof);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** 등록 요청만 주기적으로 다시 본다(요청은 다른 기기 앞의 사람이 보낸다). 가려진 탭은 건너뛴다. */
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      api
        .getPendingClaims()
        .then(setClaims)
        .catch(() => {});
    }, CLAIM_POLL_MS);
    return () => clearInterval(id);
  }, [api]);

  /**
   * 조작의 공통 껍데기 — 자리 잠금 → 실행 → 알림 → 목록 재조회 → 오류 노출.
   * 낙관적 갱신 대신 재조회한다(서버가 진실원이고 개수·가시성이 함께 바뀐다).
   */
  const run = useCallback(
    async (key: string, action: () => Promise<string>) => {
      setBusyKey(key);
      setError('');
      setNotice('');
      try {
        setNotice(await action());
        await refresh();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyKey('');
      }
    },
    [refresh],
  );

  const tree = useMemo(() => buildTree(groups, agents, entries), [groups, agents, entries]);
  const pathOf = useCallback((id: string) => groupPath(groups, id) || '그룹', [groups]);

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      saveCollapsed(safeStorage(), COLLAPSE_SCOPE, next);
      return next;
    });
  }, []);

  // ── 기기 행 조작(종전과 같다) ──────────────────────────────────────────────

  /** 기기 전체 차단/해제 — **차단**이 진행 중 세션을 끊는 파급이 커서 그쪽에서 확인을 받는다. */
  const toggleDisabled = useCallback(
    async (agent: AgentInfo) => {
      const next = !agent.disabled;
      if (next) {
        const ok = await confirmText(
          `'${agent.deviceName || agent.deviceCode}' 기기를 차단할까요?\n\n` +
            `우리 조직 사용자의 접속이 즉시 거부되고, 진행 중인 우리 조직의 원격 세션이 있으면 강제 종료됩니다.\n` +
            `(이 기기가 등록된 다른 조직과 코드 접속에는 영향이 없습니다.)\n` +
            `언제든 다시 해제할 수 있습니다(기기 재등록 불필요).`,
        );
        if (!ok) return;
      }
      await run(`agent:${agent.id}`, async () => {
        const r = await api.setAgentDisabled(agent.id, next);
        return next ? `차단했습니다. 종료된 세션 ${r.closedSessions}건${r.disconnected ? ', 연결 강제 종료' : ''}.` : '차단을 해제했습니다.';
      });
    },
    [api, run],
  );

  /**
   * 코드(익명) 접속만 차단/허용. ⚠️ 확인창 방향이 위와 반대 — 여기서는 *허용*이 노출을 늘리는 위험한 방향이다
   * (기기 코드는 비밀이 아니다).
   */
  const toggleAnonDisabled = useCallback(
    async (agent: AgentInfo) => {
      const nextDisabled = !agent.anonDisabled;
      if (!nextDisabled) {
        const ok = await confirmText(
          `'${agent.deviceName || agent.deviceCode}' 기기의 코드 접속을 다시 허용할까요?\n\n` +
            `기기 코드(${agent.deviceCode})를 아는 사람은 누구나 이 기기에 접속을 요청할 수 있게 됩니다.\n` +
            `접속에는 여전히 비밀번호가 필요하지만, 코드 자체는 비밀이 아닙니다.`,
        );
        if (!ok) return;
      }
      await run(`agent:${agent.id}`, async () => {
        await api.setAgentAnonDisabled(agent.id, nextDisabled);
        return nextDisabled ? '코드 접속을 차단했습니다. 등록 사용자 접속은 그대로 열려 있습니다.' : '코드 접속을 다시 허용했습니다.';
      });
    },
    [api, run],
  );

  /** [조직에서 제거](§14.18) — 되돌릴 수 없어(다시 등록 필요) 확인을 받는다. */
  const removeFromOrg = useCallback(
    async (agent: AgentInfo) => {
      const ok = await confirmAction({
        title: `'${agent.deviceName || agent.deviceCode}' 기기를 조직에서 제거할까요?`,
        message:
          '우리 조직의 기기 목록에서 사라지고, 이 기기에 준 사용자 권한과 진행 중인 우리 조직의 세션이 정리됩니다. ' +
          '다시 쓰려면 기기에서 등록 요청을 새로 받아야 합니다(다른 조직의 등록은 그대로입니다).',
        confirmLabel: '제거',
        danger: true,
      });
      if (!ok) return;
      await run(`agent:${agent.id}`, async () => {
        const r = await api.removeAgentFromOrg(agent.id);
        return `조직에서 제거했습니다. 종료된 세션 ${r.closedSessions}건.`;
      });
    },
    [api, run],
  );

  /**
   * 이름 변경(인라인 — 트리 전체에서 한 번에 하나). 열린 행 = 행 키(`agent:` · `entry:` · `book:` + id).
   * 🖥 관리 기기 · 🔖 조직 공유 기기 · 내 주소록이 같은 상태와 편집 칸(`RenameField`)을 쓴다(2026-10-12 — ID로 추가한 기기에도 이름 변경).
   */
  const [renamingKey, setRenamingKey] = useState('');
  const [renameInput, setRenameInput] = useState('');

  /**
   * 행 하나의 이름 변경 상태를 만든다.
   * @param key 행 키(작업 잠금 키와 같다)
   * @param current 지금 이름 — 그대로면 저장을 부르지 않는다(관리 기기는 서버가 renamedAt 을 새로 찍는다)
   * @param save 새 이름 저장 → 알림 문구
   */
  const renameStateFor = useCallback(
    (key: string, current: string, save: (next: string) => Promise<string>): RenameState => ({
      active: renamingKey === key,
      value: renameInput,
      onChange: setRenameInput,
      onStart: () => {
        setRenamingKey(key);
        setRenameInput(current);
      },
      onSubmit: () => {
        const next = renameInput.trim();
        if (!next || next === current) {
          setRenamingKey('');
          return;
        }
        void run(key, async () => {
          const said = await save(next);
          setRenamingKey('');
          return said;
        });
      },
      onCancel: () => setRenamingKey(''),
    }),
    [renameInput, renamingKey, run],
  );

  // ── 그룹 이동(단추 · 끌어다 놓기 공용) ─────────────────────────────────────

  const moveAgent = useCallback(
    (agentId: string, groupId: string) => {
      const a = agents.find((x) => x.id === agentId);
      if (!a || a.groupId === groupId) return;
      void run(`agent:${agentId}`, async () => {
        await api.moveDeviceToGroup(agentId, groupId);
        setMoving('');
        return `'${a.deviceName || a.deviceCode}' 을(를) '${pathOf(groupId)}' 로 옮겼습니다.`;
      });
    },
    [agents, api, pathOf, run],
  );

  const moveEntry = useCallback(
    (entryId: string, groupId: string) => {
      const e = entries.find((x) => x.id === entryId);
      if (!e || e.groupId === groupId) return;
      void run(`entry:${entryId}`, async () => {
        await api.updateOrgEntry(entryId, { groupId });
        setMoving('');
        return `'${e.alias}' 을(를) '${pathOf(groupId)}' 로 옮겼습니다.`;
      });
    },
    [api, entries, pathOf, run],
  );

  /** @param parentId null = 최상위로 */
  const moveGroup = useCallback(
    (groupId: string, parentId: string | null) => {
      const problem = groupMoveProblem(groups, groupId, parentId);
      if (problem) {
        setError(problem);
        return;
      }
      void run(`group:${groupId}`, async () => {
        await api.moveGroup(groupId, parentId);
        setGroupForm(null);
        return `'${pathOf(groupId)}' 그룹을 ${parentId ? `'${pathOf(parentId)}' 아래로` : '최상위로'} 옮겼습니다.`;
      });
    },
    [api, groups, pathOf, run],
  );

  const onDrop = useCallback(
    (target: string, item: TreeDragItem) => {
      if (item.kind === 'agent') moveAgent(item.id, target);
      else if (item.kind === 'entry') moveEntry(item.id, target);
      else moveGroup(item.id, target);
    },
    [moveAgent, moveEntry, moveGroup],
  );

  // ── 그룹 메뉴 동작 ─────────────────────────────────────────────────────────

  const issueToken = useCallback(
    (groupId: string) => {
      void run(`group:${groupId}`, async () => {
        const t = await api.issueEnrollmentToken(groupId);
        setTokenShown({ groupId, token: t });
        return `'${pathOf(groupId)}' 에 기기를 등록할 토큰을 발급했습니다.`;
      });
    },
    [api, pathOf, run],
  );

  const deleteGroup = useCallback(
    async (node: GroupNode) => {
      const g = node.group;
      const ok = await confirmAction({
        title: `'${pathOf(g.id)}' 그룹을 삭제할까요?`,
        message:
          g.activeTokenCount > 0
            ? `이 그룹에 기기를 등록하려고 발급한 유효한 등록 토큰 ${g.activeTokenCount}개도 함께 무효가 됩니다.`
            : '비어 있는 그룹입니다. 되돌릴 수 없습니다.',
        confirmLabel: '삭제',
        danger: true,
      });
      if (!ok) return;
      void run(`group:${g.id}`, async () => {
        const r = await api.deleteGroup(g.id);
        return `'${g.name}' 그룹을 삭제했습니다.${r.revokedTokens ? ` 등록 토큰 ${r.revokedTokens}개가 무효가 됐습니다.` : ''}`;
      });
    },
    [api, pathOf, run],
  );

  /** 그룹 메뉴 항목 — 데이터로 만들어 메뉴 모양과 떼어 둔다. 못 쓰는 항목은 이유와 함께 잠근다. */
  const menuFor = useCallback(
    (node: GroupNode): MenuItem[] => {
      const id = node.group.id;
      const open = (kind: GroupForm['kind']) => () => {
        setGroupForm({ groupId: id, kind });
        setCollapsed((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      };
      return [
        ...(SHOW_GROUP_ENROLL_TOKEN ? [{ label: '이 그룹에 기기 등록', onSelect: () => issueToken(id) }] : []),
        // 화면 문구는 "기기 추가"(2026-10-12 사용자 요청) — 코드상 이름은 공유 ID 항목(entry) 그대로.
        { label: '기기 추가', onSelect: open('entry') },
        {
          label: '하위 그룹 만들기',
          onSelect: open('child'),
          disabledReason: canAddChild(groups, id) ? null : `그룹은 ${GROUP_TREE_MAX_DEPTH}단계까지 만들 수 있습니다.`,
        },
        { label: '이름 변경', onSelect: open('rename') },
        { label: '그룹 이동', onSelect: open('move') },
        { label: '삭제', onSelect: () => void deleteGroup(node), danger: true, disabledReason: groupDeleteProblem(groups, id) },
      ];
    },
    [deleteGroup, groups, issueToken],
  );

  /** 그룹 머리 아래 칸 — 열린 입력 폼과 발급한 토큰. */
  const groupExtra = useCallback(
    (node: GroupNode) => {
      const id = node.group.id;
      const form = groupForm?.groupId === id ? groupForm : null;
      const formBusy = busyKey === `group:${id}`;
      const close = () => setGroupForm(null);
      return (
        <>
          {tokenShown?.groupId === id && (
            <EnrollTokenNotice token={tokenShown.token} groupLabel={pathOf(id)} onClose={() => setTokenShown(null)} />
          )}
          {form?.kind === 'child' && (
            <NameForm
              placeholder="하위 그룹 이름"
              saveLabel="만들기"
              busy={formBusy}
              onCancel={close}
              onSave={(name) =>
                void run(`group:${id}`, async () => {
                  await api.createGroup(name, id);
                  close();
                  return `'${pathOf(id)}' 아래에 '${name}' 그룹을 만들었습니다.`;
                })
              }
            />
          )}
          {form?.kind === 'rename' && (
            <NameForm
              initial={node.group.name}
              placeholder="그룹 이름"
              busy={formBusy}
              onCancel={close}
              onSave={(name) =>
                name === node.group.name
                  ? close()
                  : void run(`group:${id}`, async () => {
                      await api.renameGroup(id, name);
                      close();
                      return `그룹 이름을 '${name}' 으로 바꿨습니다.`;
                    })
              }
            />
          )}
          {form?.kind === 'entry' && (
            <EntryForm
              busy={formBusy}
              hint="조직 구성원 모두에게 보입니다. 비밀번호는 저장하지 않습니다(접속할 때마다 입력)."
              onCancel={close}
              onSave={(code, alias) =>
                void run(`group:${id}`, async () => {
                  await api.createOrgEntry(code, alias, id);
                  close();
                  return `'${pathOf(id)}' 에 기기 '${alias}' 을(를) 추가했습니다.`;
                })
              }
            />
          )}
          {form?.kind === 'move' && (
            <GroupPicker
              groups={groups}
              allowRoot
              initial={node.group.parentId ?? ROOT_OPTION}
              busy={formBusy}
              disabledReason={(v) => groupMoveProblem(groups, id, v === ROOT_OPTION ? null : v)}
              onCancel={close}
              onConfirm={(v) => moveGroup(id, v === ROOT_OPTION ? null : v)}
            />
          )}
        </>
      );
    },
    [api, busyKey, groupForm, groups, moveGroup, pathOf, run, tokenShown],
  );

  // ── 행 그리기 ─────────────────────────────────────────────────────────────

  const renderAgent = useCallback(
    (a: AgentInfo) => {
      const key = `agent:${a.id}`;
      return (
        <AgentRow
          a={a}
          isAdmin={isAdmin}
          busy={busy}
          isSelf={isSameDeviceCode(a.deviceCode, myCode)}
          rowBusy={busyKey === key}
          pw={pw}
          rename={renameStateFor(key, a.deviceName || '', async (next) => {
            const r = await api.renameAgent(a.id, next);
            return `기기 이름을 '${r.deviceName}' 으로 바꿨습니다. 이 이름은 기기가 다시 접속해도 유지됩니다.`;
          })}
          picker={
            moving === key ? (
              <GroupPicker
                groups={groups}
                initial={a.groupId ?? undefined}
                busy={busyKey === key}
                disabledReason={(v) => (v === a.groupId ? '지금 있는 그룹입니다.' : null)}
                onCancel={() => setMoving('')}
                onConfirm={(v) => moveAgent(a.id, v)}
              />
            ) : null
          }
          onToggleAnon={() => void toggleAnonDisabled(a)}
          onToggleDisabled={() => void toggleDisabled(a)}
          onRemoveFromOrg={() => void removeFromOrg(a)}
          onStartMove={() => setMoving(key)}
        />
      );
    },
    [api, busy, busyKey, groups, isAdmin, moveAgent, moving, myCode, pw, removeFromOrg, renameStateFor, toggleAnonDisabled, toggleDisabled],
  );

  const renderEntry = useCallback(
    (e: OrgEntryInfo) => {
      const key = `entry:${e.id}`;
      const status = e.managedAgentId
        ? `관리 기기로도 등록됨${e.online ? ' · 온라인' : ''}`
        : '조직 공유';
      return (
        <EntryRow
          dataId={e.id}
          alias={e.alias}
          deviceCode={e.deviceCode}
          online={e.online}
          status={status}
          busy={busy}
          isSelf={isSameDeviceCode(e.deviceCode, myCode)}
          pw={pw}
          rowBusy={busyKey === key}
          // 조직 공유 기기 이름은 관리자만 바꾼다(서버도 403) — 일반 사용자에겐 단추가 없다.
          rename={
            isAdmin
              ? renameStateFor(key, e.alias, async (next) => {
                  await api.updateOrgEntry(e.id, { alias: next });
                  return `기기 이름을 '${next}' 으로 바꿨습니다.`;
                })
              : undefined
          }
          picker={
            moving === key ? (
              <GroupPicker
                groups={groups}
                initial={e.groupId}
                busy={busyKey === key}
                disabledReason={(v) => (v === e.groupId ? '지금 있는 그룹입니다.' : null)}
                onCancel={() => setMoving('')}
                onConfirm={(v) => moveEntry(e.id, v)}
              />
            ) : null
          }
          actions={
            isAdmin && (
              <>
                <button className="ghost" onClick={() => setMoving(key)} disabled={busyKey === key}>
                  그룹 이동
                </button>
                <button
                  className="ghost danger"
                  disabled={busyKey === key}
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: `기기 '${e.alias}' 을(를) 목록에서 삭제할까요?`,
                      message: '조직 구성원 모두의 목록에서 사라집니다. 원격 기기 자체에는 영향이 없습니다.',
                      confirmLabel: '삭제',
                      danger: true,
                    });
                    if (!ok) return;
                    void run(key, async () => {
                      await api.deleteOrgEntry(e.id);
                      return `기기 '${e.alias}' 을(를) 목록에서 삭제했습니다.`;
                    });
                  }}
                >
                  삭제
                </button>
              </>
            )
          }
        />
      );
    },
    [api, busy, busyKey, groups, isAdmin, moveEntry, moving, myCode, pw, renameStateFor, run],
  );

  // ── 등록 요청 ─────────────────────────────────────────────────────────────

  /** 수락/거부 → 목록 갱신. @param groupId 관리자가 고른 그룹(일반 사용자는 서버가 기본 그룹으로) */
  const resolveClaim = useCallback(
    async (requestId: string, accept: boolean, groupId?: string) => {
      setClaimBusyId(requestId);
      setError('');
      setNotice('');
      try {
        await api.resolveClaim(requestId, accept, groupId);
        setClaimPicking('');
        setNotice(accept ? `기기를 등록했습니다${groupId ? ` — '${pathOf(groupId)}'` : ''}.` : '등록 요청을 거부했습니다.');
        await refresh();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setClaimBusyId('');
      }
    },
    [api, pathOf, refresh],
  );

  const rootForm = groupForm?.groupId === null ? groupForm : null;
  const nothing = tree.roots.length === 0 && tree.orphans.agents.length === 0 && tree.orphans.entries.length === 0;

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>기기</h2>
        <div className="row">
          {isAdmin && (
            <button className="ghost" onClick={() => setGroupForm({ groupId: null, kind: 'child' })} title="최상위에 새 그룹을 만듭니다">
              + 그룹
            </button>
          )}
          <button className="ghost" onClick={refresh} disabled={loading}>
            {loading ? '불러오는 중…' : '새로고침'}
          </button>
        </div>
      </div>

      {/* 기기 등록 요청(claim) — 기기 쪽에서 "이 조직에 넣어 달라"고 보낸 것(토큰을 미리 나눠 줄 수 없는 상황용). */}
      <div className="enroll-panel">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="name">
              기기 등록 요청
              {claims.length > 0 && <span className="hint" style={{ marginLeft: 8 }}>대기 {claims.length}건</span>}
            </div>
            <div className="hint">
              기기 앱에서 내 이메일로 보낸 요청입니다. 수락하면 이 조직에 등록됩니다.
              {isAdmin ? ' 들어갈 그룹을 고를 수 있습니다.' : ''}
            </div>
          </div>
        </div>

        {/* 내 이메일 = 등록 요청이 오는 주소. 변경은 [내 계정](사이드바 아래 이름)으로 옮겼다(2026-10-23). */}
        {me && (
          <div className="row" style={{ gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="hint">내 이메일</span>
            <b>{me.email}</b>
            <span className="hint">— 변경은 왼쪽 아래 이름을 눌러 [내 계정]에서</span>
          </div>
        )}

        {claims.length > 0 && (
          <div className="list" style={{ marginTop: 10 }}>
            {claims.map((c) => (
              <div key={c.requestId} className="list-item">
                <span className="dot" />
                <div style={{ flex: 1 }}>
                  <div className="name">{c.deviceName || '(이름 없음)'}</div>
                  <div className="hint">
                    {c.deviceCode} · {new Date(c.expiresAt).toLocaleTimeString()} 까지 유효
                  </div>
                </div>
                {claimPicking === c.requestId ? (
                  <GroupPicker
                    groups={groups}
                    confirmLabel="이 그룹으로 수락"
                    busy={claimBusyId === c.requestId}
                    onCancel={() => setClaimPicking('')}
                    onConfirm={(v) => void resolveClaim(c.requestId, true, v)}
                  />
                ) : (
                  <>
                    <button
                      onClick={() =>
                        // 관리자이고 그룹이 여럿이면 고르게 한다. 그 밖엔 서버가 기본 그룹으로.
                        isAdmin && groups.length > 1 ? setClaimPicking(c.requestId) : void resolveClaim(c.requestId, true)
                      }
                      disabled={claimBusyId === c.requestId}
                    >
                      수락
                    </button>
                    <button className="ghost" onClick={() => void resolveClaim(c.requestId, false)} disabled={claimBusyId === c.requestId}>
                      거부
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="hint" style={{ color: 'var(--err)', margin: '12px 0' }}>{error}</div>}
      {notice && <div className="hint" style={{ margin: '12px 0' }}>{notice}</div>}

      {rootForm && (
        <div style={{ margin: '12px 0' }}>
          <NameForm
            placeholder="새 그룹 이름(최상위)"
            saveLabel="만들기"
            busy={busyKey === 'group:root'}
            onCancel={() => setGroupForm(null)}
            onSave={(name) =>
              void run('group:root', async () => {
                await api.createGroup(name);
                setGroupForm(null);
                return `'${name}' 그룹을 만들었습니다.`;
              })
            }
          />
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        {nothing && !loading ? (
          <p className="hint">표시할 기기가 없습니다.</p>
        ) : (
          <GroupTree
            roots={tree.roots}
            collapsed={collapsed}
            onToggle={toggleGroup}
            draggable={isAdmin}
            onDrop={onDrop}
            renderAgent={renderAgent}
            renderEntry={renderEntry}
            groupActions={isAdmin ? (n) => <GroupMenu items={menuFor(n)} label={`'${n.group.name}' 그룹 메뉴`} /> : undefined}
            groupExtra={groupExtra}
          />
        )}
        {/* 그룹 목록에 없는 그룹을 가리키는 항목(방금 지워진 그룹 등 경합) — 사라지지 않게 따로 보인다. */}
        {(tree.orphans.agents.length > 0 || tree.orphans.entries.length > 0) && (
          <div className="gtree" style={{ marginTop: 8 }}>
            <div className="gtree-head" style={{ cursor: 'default' }}>
              <span className="gtree-name">그룹 정보 없음</span>
              <span className="gtree-count">새로고침하면 정리됩니다</span>
            </div>
            {tree.orphans.agents.map((a) => (
              <div key={a.id} className="gtree-item">
                {renderAgent(a)}
              </div>
            ))}
            {tree.orphans.entries.map((e) => (
              <div key={e.id} className="gtree-item">
                {renderEntry(e)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 내 주소록 — 개인 ID 항목(나만 보인다). 종전 [주소록] 탭을 트리 맨 아래 가지로 옮겼다(§14.20). */}
      <div className="gtree" style={{ marginTop: 12 }} data-testid="my-address-book">
        <div className="gtree-head" style={{ cursor: 'default' }}>
          <span className="gtree-icon" aria-hidden>
            👤
          </span>
          <span className="gtree-name">내 주소록</span>
          <span className="gtree-count">나만 보임 · {book.items.length}</span>
          <span className="gtree-head-actions">
            <button className="ghost" onClick={() => setBookForm(true)} disabled={bookForm}>
              + 추가
            </button>
          </span>
        </div>
        {bookForm && (
          <div className="gtree-item">
            <EntryForm
              busy={busyKey === 'book'}
              hint="나에게만 보입니다. 비밀번호는 저장하지 않습니다(접속할 때마다 입력)."
              onCancel={() => setBookForm(false)}
              onSave={(code, alias) =>
                void run('book', async () => {
                  await book.add(code, alias);
                  setBookForm(false);
                  return `내 주소록에 '${alias}' 을(를) 추가했습니다.`;
                })
              }
            />
          </div>
        )}
        {book.error && <div className="hint gtree-empty" style={{ color: 'var(--err)' }}>{book.error}</div>}
        {book.items.length === 0 && !bookForm && !book.error && <div className="hint gtree-empty">저장한 항목이 없습니다.</div>}
        {book.items.map((it) => (
          <div key={it.id} className="gtree-item">
            <EntryRow
              dataId={it.id}
              alias={it.alias}
              deviceCode={it.deviceCode}
              online={it.reachable ? !!it.online : null}
              status={it.reachable ? [it.deviceName, it.online ? '온라인' : ''].filter(Boolean).join(' · ') : '조직 밖'}
              busy={busy}
              isSelf={isSameDeviceCode(it.deviceCode, myCode)}
              pw={pw}
              rowBusy={busyKey === `book:${it.id}`}
              // 내 주소록 = 본인 것이라 누구나 바꾼다. 서버는 같은 코드를 다시 저장하면 이름만 바꾼다(멱등 upsert).
              rename={renameStateFor(`book:${it.id}`, it.alias, async (next) => {
                await book.rename(it.deviceCode, next);
                return `내 주소록 이름을 '${next}' 으로 바꿨습니다.`;
              })}
              actions={
                <button
                  className="ghost"
                  disabled={busyKey === `book:${it.id}`}
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: `내 주소록에서 "${it.alias}" 을(를) 삭제할까요?`,
                      message: '기기 자체나 접속 기록에는 영향이 없습니다.',
                      confirmLabel: '삭제',
                      danger: true,
                    });
                    if (!ok) return;
                    void run(`book:${it.id}`, async () => {
                      await book.remove(it.id);
                      return `내 주소록에서 '${it.alias}' 을(를) 삭제했습니다.`;
                    });
                  }}
                >
                  삭제
                </button>
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
}
